import { z } from 'zod';

export const emailSchema = z
  .string()
  .min(3)
  .max(160)
  .email('Format email tidak valid')
  .transform((value) => value.trim().toLowerCase());

/** Aturan kata sandi baru, dipakai daftar dan atur sandi baru supaya selalu sama. */
export const newPasswordSchema = z.string().min(8, 'Kata sandi minimal 8 karakter').max(200);

export const registerSchema = z.object({
  name: z.string().min(2, 'Nama minimal 2 karakter').max(120),
  email: emailSchema,
  password: newPasswordSchema,
  phone: z.string().min(8).max(24).optional(),
});

/** Masa berlaku tautan reset kata sandi pelanggan. */
export const PASSWORD_RESET_TTL_MINUTES = 30;

/** Minta tautan reset. Jawaban API selalu sama, terdaftar atau tidak. */
export const passwordResetRequestSchema = z.object({ email: emailSchema });

/** Token dari tautan email: 32 byte acak dalam base64url (43 karakter). */
export const passwordResetConfirmSchema = z.object({
  token: z
    .string()
    .trim()
    .regex(/^[A-Za-z0-9_-]{32,128}$/, 'Tautan reset tidak sah'),
  password: newPasswordSchema,
});

export const loginSchema = z.object({
  email: emailSchema,
  password: z.string().min(1, 'Kata sandi wajib diisi').max(200),
});

/**
 * Masuk panel admin memakai username. Hanya akun STAFF/ADMIN/SUPER_ADMIN yang
 * punya username; pelanggan tetap memakai loginSchema (email).
 */
export const panelLoginSchema = z.object({
  username: z
    .string()
    .trim()
    .min(1, 'Username wajib diisi')
    .max(64)
    .transform((value) => value.toLowerCase()),
  password: z.string().min(1, 'Kata sandi wajib diisi').max(200),
});

export const updateProfileSchema = z
  .object({
    name: z.string().min(2).max(120).optional(),
    phone: z.string().min(8).max(24).optional(),
  })
  .refine((data) => data.name !== undefined || data.phone !== undefined, {
    message: 'Tidak ada perubahan yang dikirim',
  });

export type RegisterInput = z.infer<typeof registerSchema>;
export type LoginInput = z.infer<typeof loginSchema>;
export type PanelLoginInput = z.infer<typeof panelLoginSchema>;
export type UpdateProfileInput = z.infer<typeof updateProfileSchema>;
export type PasswordResetRequestInput = z.infer<typeof passwordResetRequestSchema>;
export type PasswordResetConfirmInput = z.infer<typeof passwordResetConfirmSchema>;

// --- Admin: pelanggan ---

export const userRoleSchema = z.enum(['CUSTOMER', 'STAFF', 'ADMIN', 'SUPER_ADMIN']);
export const userStatusSchema = z.enum(['ACTIVE', 'SUSPENDED']);

export const adminCustomerQuerySchema = z.object({
  /** Nama, email, atau nomor telepon. */
  q: z.string().trim().max(120).optional(),
  role: userRoleSchema.optional(),
  status: userStatusSchema.optional(),
  page: z.coerce.number().int().min(1).default(1),
  limit: z.coerce.number().int().min(1).max(100).default(30),
});

export const adminCustomerParamsSchema = z.object({
  id: z.string().min(1).max(64),
});

export const adminCustomerUpdateSchema = z
  .object({
    status: userStatusSchema.optional(),
    /** Hanya ADMIN yang boleh mengubah peran; dijaga di service. */
    role: userRoleSchema.optional(),
  })
  .refine((data) => Object.keys(data).length > 0, { message: 'Tidak ada perubahan yang dikirim' });

export type UserRole = z.infer<typeof userRoleSchema>;
export type UserStatus = z.infer<typeof userStatusSchema>;
export type AdminCustomerQuery = z.infer<typeof adminCustomerQuerySchema>;
export type AdminCustomerParams = z.infer<typeof adminCustomerParamsSchema>;
export type AdminCustomerUpdate = z.infer<typeof adminCustomerUpdateSchema>;
